import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Matches the `gap` on both the visible row and the hidden copy it measures. Kept here rather than
 * read back off the DOM so the arithmetic below stays a single pass over cached widths.
 */
export const COMPACT_ITEM_GAP_PX = 4;

/**
 * A count on its own says nothing about what is in the group, so one badge always survives the fold
 * — except where the row cannot hold that badge and the count together. See `getFittedItemCount`.
 */
const MIN_VISIBLE_ITEM_COUNT = 1;

/**
 * How many of a group's badges the compact row has space for.
 *
 * Measured off a hidden copy of the whole list rather than off the row itself: dropping a badge from
 * the visible row changes what fits, so measuring the row would chase its own result instead of
 * settling on one.
 */
export function useFittedItemCount(itemCount: number) {
    const containerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const [visibleItemCount, setVisibleItemCount] = useState(itemCount);

    const measure = useCallback(() => {
        const container = containerRef.current;
        const measureLayer = measureRef.current;

        if (!container || !measureLayer) {
            return;
        }

        setVisibleItemCount(getFittedItemCount(container, measureLayer, itemCount));
    }, [itemCount]);

    useLayoutEffect(() => {
        const container = containerRef.current;

        if (!container) {
            return;
        }

        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(container);

        return () => observer.disconnect();
    }, [measure]);

    return { containerRef, measureRef, visibleItemCount };
}

/**
 * The hidden copy holds every badge followed by the widest overflow badge the group could need, so
 * one read of it answers both "how wide is each" and "how much has to be held back for the count".
 */
export function getFittedItemCount(container: HTMLElement, measureLayer: HTMLElement, itemCount: number): number {
    const availableWidth = container.clientWidth;
    const nodes = Array.from(measureLayer.children);

    const itemWidths = nodes.slice(0, itemCount).map((node) => node.getBoundingClientRect().width);
    const overflowWidth = nodes[itemCount]?.getBoundingClientRect().width ?? 0;

    const gapsWidth = COMPACT_ITEM_GAP_PX * Math.max(itemWidths.length - 1, 0);
    const totalWidth = itemWidths.reduce((sum, width) => sum + width, 0) + gapsWidth;

    // Everything fits, so no room needs holding back for a count that will not be shown.
    if (totalWidth <= availableWidth) {
        return itemCount;
    }

    let usedWidth = 0;
    let fittedCount = 0;

    for (const width of itemWidths) {
        const widthWithItem = usedWidth + width + (fittedCount > 0 ? COMPACT_ITEM_GAP_PX : 0);

        // Whatever this one leaves behind is spoken for by the overflow badge, so its width has to
        // fit alongside.
        if (widthWithItem + COMPACT_ITEM_GAP_PX + overflowWidth > availableWidth) {
            break;
        }

        usedWidth = widthWithItem;
        fittedCount += 1;
    }

    // The count is the only way into the rest of the group, and the row clips rather than wraps — so
    // where a badge and the count cannot both stand, the count takes the row. Badges do not shrink,
    // so keeping one here would push the count out of sight and strand everything behind it.
    //
    // A count of no width is a row that has not been laid out yet rather than one too narrow for it,
    // and keeps the badge.
    const isOverflowMeasured = overflowWidth > 0 && overflowWidth <= availableWidth;

    if (!fittedCount && isOverflowMeasured) {
        return 0;
    }

    return Math.max(fittedCount, MIN_VISIBLE_ITEM_COUNT);
}
